(() => {
  'use strict';

  if (window.__mascalmaProductPageLoaded) {
    if (typeof window.__mascalmaInitProductPages === 'function') {
      window.__mascalmaInitProductPages(document);
    }
    return;
  }

  window.__mascalmaProductPageLoaded = true;

  const normalize = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[·•]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MascalmaProductPage {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.mc-product-form');
      this.productData = this.readProductData();
      this.offerCards = Array.from(root.querySelectorAll('[data-offer-card]'));
      this.offerRadios = Array.from(root.querySelectorAll('[data-offer-radio]'));
      this.sizeSelect = root.querySelector('[data-size-select]');
      this.colorSelect = root.querySelector('[data-color-select]');
      this.variantInput = root.querySelector('[data-variant-id]');
      this.addButton = root.querySelector('[data-add-to-cart]');
      this.addLabel = root.querySelector('[data-add-label]');
      this.formMessage = root.querySelector('[data-form-message]');
      this.priceWarning = root.querySelector('[data-price-warning]');
      this.mobileBar = root.querySelector('[data-mobile-bar]');
      this.currentVariant = null;
      this.optionPositions = this.findOptionPositions();
      this.editorWarnings = new Set();
      this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
        style: 'currency',
        currency: this.productData?.currency || 'EUR'
      });
    }

    init() {
      this.setupVideos();
      this.setupCarousels();

      if (!this.form || !this.productData || !this.sizeSelect || !this.colorSelect) return;

      this.bindOfferControls();
      this.bindVariantControls();
      this.applyInitialSelection();
      this.setupProductForm();
      this.setupMobileBar();
    }

    readProductData() {
      const script = this.root.querySelector('[data-product-json]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    findOptionPositions() {
      const positions = { offer: -1, size: -1, color: -1 };
      if (!this.productData?.options) return positions;

      this.productData.options.forEach((option, index) => {
        const name = normalize(typeof option === 'string' ? option : option.name);
        const position = Number(typeof option === 'string' ? index : option.position);

        if (name === 'oferta' || name.includes('pack')) positions.offer = position;
        if (name === 'talla' || name.includes('tamano')) positions.size = position;
        if (name === 'color' || name.includes('color')) positions.color = position;
      });

      return positions;
    }

    bindOfferControls() {
      this.offerCards.forEach((card) => {
        const radio = card.querySelector('[data-offer-radio]');
        const toggle = card.querySelector('[data-offer-toggle]');

        radio?.addEventListener('change', () => {
          if (!radio.checked) return;
          this.setSelectedCard(card, true);
          this.syncVariant(true);
        });

        card.addEventListener('click', (event) => {
          if (event.target.closest('button, a, input, label') || !radio || radio.disabled) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });

        toggle?.addEventListener('click', () => {
          const willOpen = toggle.getAttribute('aria-expanded') !== 'true';

          this.offerCards.forEach((offerCard) => {
            this.setOfferDetails(offerCard, offerCard === card && willOpen);
          });
        });
      });
    }

    bindVariantControls() {
      this.sizeSelect.addEventListener('change', () => this.syncVariant(true));
      this.colorSelect.addEventListener('change', () => this.syncVariant(true));
    }

    applyInitialSelection() {
      const variantId = Number(new URL(window.location.href).searchParams.get('variant'));
      const linkedVariant = this.productData.variants.find((variant) => Number(variant.id) === variantId);

      if (linkedVariant) {
        this.applyVariantOptions(linkedVariant);
      } else {
        const defaultRadio = this.offerRadios.find(
          (radio) => radio.closest('[data-offer-card]')?.dataset.offerKey === 'total'
        );

        if (defaultRadio) defaultRadio.checked = true;

        this.selectMatchingOption(this.sizeSelect, 'Mini · hasta 5 kg');
        this.selectMatchingOption(this.colorSelect, 'Azul');
      }

      const checked = this.offerRadios.find((radio) => radio.checked)
        || this.offerRadios[2]
        || this.offerRadios[0];

      if (checked) {
        checked.checked = true;
        this.setSelectedCard(checked.closest('[data-offer-card]'), true);
      }

      this.syncVariant(true);
    }

    applyVariantOptions(variant) {
      const offerValue = variant.options[this.optionPositions.offer];
      const sizeValue = variant.options[this.optionPositions.size];
      const colorValue = variant.options[this.optionPositions.color];

      const radio = this.offerRadios.find(
        (item) => normalize(item.value) === normalize(offerValue)
      );

      if (radio) radio.checked = true;

      this.selectMatchingOption(this.sizeSelect, sizeValue);
      this.selectMatchingOption(this.colorSelect, colorValue);
    }

    selectMatchingOption(select, value) {
      if (!select || value == null) return;

      const option = Array.from(select.options).find(
        (item) => normalize(item.value) === normalize(value)
      );

      if (option) select.value = option.value;
    }

    setSelectedCard(selectedCard, openDetails) {
      this.offerCards.forEach((card) => {
        const selected = card === selectedCard;

        card.classList.toggle('is-selected', selected);
        this.setOfferDetails(card, selected && openDetails);
      });
    }

    setOfferDetails(card, open) {
      const toggle = card.querySelector('[data-offer-toggle]');
      const details = card.querySelector('[data-offer-details]');

      if (!toggle || !details) return;

      toggle.setAttribute('aria-expanded', String(open));
      details.hidden = !open;
    }

    findVariant(offerValue, sizeValue, colorValue) {
      const { offer, size, color } = this.optionPositions;

      if ([offer, size, color].some((position) => position < 0)) return null;

      return this.productData.variants.find((variant) => (
        normalize(variant.options[offer]) === normalize(offerValue)
        && normalize(variant.options[size]) === normalize(sizeValue)
        && normalize(variant.options[color]) === normalize(colorValue)
      )) || null;
    }

    syncVariant(updateUrl) {
      const selectedRadio = this.offerRadios.find((radio) => radio.checked);
      if (!selectedRadio) return;

      this.editorWarnings.clear();
      this.updateAllOfferPrices();

      const variant = this.findVariant(
        selectedRadio.value,
        this.sizeSelect.value,
        this.colorSelect.value
      );

      this.currentVariant = variant;

      this.updateLineProperties(selectedRadio.value);
      this.updatePurchaseState(
        variant,
        selectedRadio.closest('[data-offer-card]')
      );
      this.renderEditorWarnings();

      if (updateUrl) {
        const url = new URL(window.location.href);

        if (variant) {
          url.searchParams.set('variant', variant.id);
        } else {
          url.searchParams.delete('variant');
        }

        window.history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('mascalma:variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    updateAllOfferPrices() {
      const sizeKey = this.getSizeKey(this.sizeSelect.value);

      this.offerCards.forEach((card) => {
        const radio = card.querySelector('[data-offer-radio]');

        const variant = this.findVariant(
          card.dataset.optionValue,
          this.sizeSelect.value,
          this.colorSelect.value
        );

        const priceElement = card.querySelector('[data-offer-price]');
        const compareElement = card.querySelector('[data-offer-compare]');
        const savingElement = card.querySelector('[data-offer-saving]');

        if (!variant) {
          card.classList.add('mc-offer--unavailable');

          if (radio) radio.disabled = true;
          if (priceElement) priceElement.textContent = 'No disponible';
          if (compareElement) compareElement.hidden = true;
          if (savingElement) savingElement.textContent = 'Combinación no disponible';

          return;
        }

        card.classList.toggle('mc-offer--unavailable', !variant.available);

        if (radio) radio.disabled = !variant.available;

        if (priceElement) {
          priceElement.textContent = this.formatMoney(variant.price);
        }

        if (compareElement) {
          const hasCompare = variant.compare_at_price > variant.price;

          compareElement.hidden = !hasCompare;
          compareElement.textContent = hasCompare
            ? this.formatMoney(variant.compare_at_price)
            : '';
        }

        if (savingElement) {
          savingElement.textContent = variant.compare_at_price > variant.price
            ? `Ahorras ${this.formatMoney(variant.compare_at_price - variant.price)}`
            : variant.available
              ? 'Precio final'
              : 'Agotado';
        }

        const configuredPrice = Number(card.dataset[`price${sizeKey}`]);
        const configuredCompare = Number(card.dataset[`compare${sizeKey}`]);

        if (
          configuredPrice !== Number(variant.price)
          || configuredCompare !== Number(variant.compare_at_price || 0)
        ) {
          const offerName = card.querySelector('.mc-offer__name')?.textContent.trim()
            || card.dataset.optionValue;

          this.editorWarnings.add(
            `${offerName}: el precio configurado no coincide con la variante de Shopify.`
          );
        }
      });
    }

    getSizeKey(value) {
      const size = normalize(value);

      if (size.includes('mini')) return 'Mini';
      if (size.includes('mediana') || size.includes('medium')) return 'Medium';

      return 'Large';
    }

    updateLineProperties(offerValue) {
      const offerProperty = this.form.querySelector('[data-property-offer]');
      const sizeProperty = this.form.querySelector('[data-property-size]');
      const colorProperty = this.form.querySelector('[data-property-color]');

      if (offerProperty) offerProperty.value = offerValue;
      if (sizeProperty) sizeProperty.value = this.sizeSelect.value;
      if (colorProperty) colorProperty.value = this.colorSelect.value;
    }

    updatePurchaseState(variant, card) {
      const packName = card?.querySelector('.mc-offer__name')?.textContent.trim()
        || 'MASCALMA';

      const available = Boolean(variant?.available);

      if (this.variantInput) {
        this.variantInput.value = variant?.id || '';
      }

      if (this.addButton) {
        this.addButton.disabled = !available;
      }

      if (this.addLabel) {
        this.addLabel.textContent = available
          ? 'Añadir al carrito'
          : variant
            ? 'Agotado'
            : 'Combinación no disponible';
      }

      this.setText('[data-buy-pack]', packName);
      this.setText('[data-sticky-pack]', packName);

      if (!variant) {
        this.setText('[data-buy-price]', 'No disponible');
        this.setText('[data-sticky-price]', 'No disponible');

        this.setCompare('[data-buy-compare]', 0, 0);
        this.setCompare('[data-sticky-compare]', 0, 0);

        this.updateStickyButton(false);
        return;
      }

      this.setText('[data-buy-price]', this.formatMoney(variant.price));
      this.setText('[data-sticky-price]', this.formatMoney(variant.price));

      this.setCompare(
        '[data-buy-compare]',
        variant.compare_at_price,
        variant.price
      );

      this.setCompare(
        '[data-sticky-compare]',
        variant.compare_at_price,
        variant.price
      );

      this.updateSaleBadge(variant);
      this.updateStickyButton(available);
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }

    setCompare(selector, compareAtPrice, price) {
      this.root.querySelectorAll(selector).forEach((element) => {
        const visible = Number(compareAtPrice) > Number(price);

        element.hidden = !visible;
        element.textContent = visible
          ? this.formatMoney(compareAtPrice)
          : '';
      });
    }

    updateSaleBadge(variant) {
      const badge = this.root.querySelector('[data-sale-badge]');
      if (!badge) return;

      const visible = variant.compare_at_price > variant.price;

      badge.hidden = !visible;

      if (visible) {
        const discount = Math.round(
          (1 - variant.price / variant.compare_at_price) * 100
        );

        badge.textContent = `-${discount}% solo hoy`;
      }
    }

    updateStickyButton(available) {
      const button = this.root.querySelector('[data-sticky-submit]');
      if (!button) return;

      button.disabled = !available;
      button.textContent = available
        ? 'Añadir al carrito'
        : 'Agotado';
    }

    renderEditorWarnings() {
      if (!this.priceWarning) return;

      if (
        this.optionPositions.offer < 0
        || this.optionPositions.size < 0
        || this.optionPositions.color < 0
      ) {
        this.editorWarnings.add(
          'Revisa que las opciones del producto se llamen Oferta, Talla y Color.'
        );
      }

      this.priceWarning.hidden = this.editorWarnings.size === 0;

      this.priceWarning.textContent = this.editorWarnings.size
        ? `Aviso del editor: ${Array.from(this.editorWarnings).join(' ')}`
        : '';
    }

    formatMoney(cents) {
      return this.moneyFormatter.format(Number(cents || 0) / 100);
    }

    setupProductForm() {
      this.form.addEventListener('submit', async (event) => {
        if (!this.currentVariant?.available) {
          event.preventDefault();

          this.showFormMessage(
            'Esta combinación no está disponible.',
            true
          );

          return;
        }

        const cart = document.querySelector('cart-drawer')
          || document.querySelector('cart-notification');

        if (
          !cart
          || typeof cart.renderContents !== 'function'
          || typeof cart.getSectionsToRender !== 'function'
        ) {
          return;
        }

        event.preventDefault();

        this.setLoading(true);
        this.showFormMessage('', false);

        try {
          const formData = new FormData(this.form);

          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          const response = await fetch(this.form.action, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(
              result.description
              || result.message
              || 'No se ha podido añadir el producto.'
            );
          }

          cart.renderContents(result);
          this.showFormMessage('Añadido al carrito.', false);
        } catch (error) {
          this.showFormMessage(
            error.message
            || 'No se ha podido añadir el producto. Inténtalo de nuevo.',
            true
          );
        } finally {
          this.setLoading(false);
        }
      });
    }

    setLoading(loading) {
      this.form.classList.toggle('is-loading', loading);

      if (this.addButton) {
        this.addButton.disabled = (
          loading
          || !this.currentVariant?.available
        );
      }
    }

    showFormMessage(message, isError) {
      if (!this.formMessage) return;

      this.formMessage.textContent = message;
      this.formMessage.classList.toggle('is-error', isError);
    }

    setupMobileBar() {
      if (!this.mobileBar || !this.addButton) return;

      const stickyButton = this.mobileBar.querySelector(
        '[data-sticky-submit]'
      );

      const footerTargets = Array.from(
        new Set(
          document.querySelectorAll(
            '.shopify-section-group-footer-group, footer.footer, footer[role="contentinfo"]'
          )
        )
      );

      stickyButton?.addEventListener('click', () => {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.addButton);
        } else {
          this.addButton.click();
        }
      });

      const update = () => {
        const buttonRect = this.addButton.getBoundingClientRect();

        const footerIsVisible = footerTargets.some((footer) => {
          const footerRect = footer.getBoundingClientRect();

          return (
            footerRect.top <= window.innerHeight + 12
            && footerRect.bottom >= 0
          );
        });

        const shouldShow = (
          window.innerWidth < 750
          && buttonRect.bottom < 0
          && !footerIsVisible
        );

        this.mobileBar.hidden = !shouldShow;
        this.root.classList.toggle(
          'has-mobile-bar',
          shouldShow
        );
      };

      if ('IntersectionObserver' in window) {
        this.mobileObserver = new IntersectionObserver(
          update,
          { threshold: 0 }
        );

        this.mobileObserver.observe(this.addButton);
      }

      window.addEventListener(
        'scroll',
        update,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        update,
        { passive: true }
      );

      update();
    }

    setupVideos() {
      this.root.querySelectorAll('video').forEach((video) => {
        video.controls = false;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;

        if (prefersReducedMotion()) {
          video.autoplay = false;
          video.pause();
        } else {
          video.autoplay = true;

          const playPromise = video.play();

          if (playPromise?.catch) {
            playPromise.catch(() => {});
          }
        }
      });
    }

    setupCarousels() {
      this.root.querySelectorAll('[data-carousel]').forEach((carousel) => {
        this.setupCarousel(carousel);
      });
    }

    setupCarousel(carousel) {
      const track = carousel.querySelector('[data-carousel-track]');

      const slides = Array.from(
        carousel.querySelectorAll(
          '[data-carousel-slide], [data-review-slide]'
        )
      );

      const previous = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      const dots = carousel.querySelector('[data-carousel-dots]');

      if (!track || slides.length === 0) return;

      let activeIndex = 0;
      let ticking = false;

      const behavior = prefersReducedMotion()
        ? 'auto'
        : 'smooth';

      const goTo = (index, scrollBehavior = behavior) => {
        const target = Math.max(
          0,
          Math.min(index, slides.length - 1)
        );

        const slide = slides[target];

        const centeredOffset = (
          slide.offsetLeft
          - ((track.clientWidth - slide.offsetWidth) / 2)
        );

        const targetLeft = carousel.dataset.carousel === 'reviews'
          ? centeredOffset
          : slide.offsetLeft;

        track.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: scrollBehavior
        });
      };

      const updateState = () => {
        const trackCenter = (
          track.scrollLeft
          + track.clientWidth / 2
        );

        let nearestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const slideCenter = (
            slide.offsetLeft
            + slide.offsetWidth / 2
          );

          const distance = Math.abs(
            trackCenter - slideCenter
          );

          if (distance < nearestDistance) {
            nearestDistance = distance;
            activeIndex = index;
          }
        });

        dots?.querySelectorAll('.mc-carousel__dot').forEach(
          (dot, index) => {
            const active = index === activeIndex;

            dot.classList.toggle('is-active', active);
            dot.setAttribute(
              'aria-current',
              active ? 'true' : 'false'
            );
          }
        );

        if (previous) {
          previous.disabled = track.scrollLeft <= 2;
        }

        if (next) {
          next.disabled = (
            track.scrollLeft
            + track.clientWidth
            >= track.scrollWidth - 2
          );
        }

        ticking = false;
      };

      if (dots) {
        dots.innerHTML = '';

        slides.forEach((slide, index) => {
          const dot = document.createElement('button');

          dot.type = 'button';
          dot.className = 'mc-carousel__dot';

          dot.setAttribute(
            'aria-label',
            `Ir al elemento ${index + 1}`
          );

          dot.addEventListener(
            'click',
            () => goTo(index)
          );

          dots.appendChild(dot);
        });
      }

      previous?.addEventListener(
        'click',
        () => goTo(activeIndex - 1)
      );

      next?.addEventListener(
        'click',
        () => goTo(activeIndex + 1)
      );

      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;

          ticking = true;
          window.requestAnimationFrame(updateState);
        },
        { passive: true }
      );

      if (slides.length <= 1) {
        if (previous) previous.hidden = true;
        if (next) next.hidden = true;
        if (dots) dots.hidden = true;
      }

      window.requestAnimationFrame(() => {
        if (
          carousel.dataset.carousel === 'reviews'
          && window.innerWidth < 750
          && slides.length >= 3
        ) {
          goTo(1, 'auto');
        }

        updateState();
      });
    }
  }

  const initProductPages = (scope = document) => {
    const roots = [];

    if (scope.matches?.('[data-mascalma-section]')) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(
      '[data-mascalma-section]'
    ).forEach((root) => {
      roots.push(root);
    });

    roots.forEach((root) => {
      if (root.dataset.mascalmaInitialized === 'true') return;

      root.dataset.mascalmaInitialized = 'true';

      const page = new MascalmaProductPage(root);

      root.mascalmaProductPage = page;
      page.init();
    });
  };

  window.__mascalmaInitProductPages = initProductPages;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initProductPages(document),
      { once: true }
    );
  } else {
    initProductPages(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initProductPages(event.target)
  );
})();